import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/utils/cn';
import { filterTriggerHover } from './filterTriggerStyles';
import { Checkbox } from './Checkbox';
import { PLATFORMS } from './PlatformSelect';

/**
 * PlatformMultiSelect Component
 *
 * Multi-select dropdown for social media platforms with brand icons and colors
 * Shows selected platforms as circular icons in the trigger button
 */

interface PlatformMultiSelectProps {
  value: string[];
  onChange: (values: string[]) => void;
  allOptionLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}

export const PlatformMultiSelect = ({
  value,
  onChange,
  allOptionLabel = 'All Platforms',
  placeholder = 'Select platforms',
  disabled = false,
  className,
  size = 'md',
}: PlatformMultiSelectProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Consume the key so a surrounding drawer/modal does not also close.
        e.preventDefault();
        setIsOpen(false);
      }
    };

    // Capture phase: runs before the bubble-phase Escape listeners of enclosing layers.
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  const isAllSelected = value.length === 0 || value.length === PLATFORMS.length;
  const selectedPlatforms = PLATFORMS.filter((p) => value.includes(p.value));

  const handleTogglePlatform = (platformValue: string) => {
    if (value.includes(platformValue)) {
      // Remove from selection
      onChange(value.filter((v) => v !== platformValue));
    } else {
      // Add to selection
      onChange([...value, platformValue]);
    }
  };

  const handleSelectAll = () => {
    onChange(PLATFORMS.map((p) => p.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const renderTriggerContent = () => {
    if (isAllSelected) {
      return (
        <>
          <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faGlobe} className="text-gray-500 text-[10px]" />
          </span>
          <span className="truncate">{allOptionLabel}</span>
        </>
      );
    }

    if (selectedPlatforms.length === 0) {
      return <span className="text-gray-400">{placeholder}</span>;
    }

    // Show up to 4 platform icons, then "+N more"
    const displayPlatforms = selectedPlatforms.slice(0, 4);
    const remainingCount = selectedPlatforms.length - 4;

    return (
      <>
        <div className="flex items-center gap-1 flex-shrink-0">
          {displayPlatforms.map((platform) => (
            <span
              key={platform.value}
              className="w-5 h-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: platform.color }}
              title={platform.label}
            >
              <FontAwesomeIcon icon={platform.icon} className="text-white text-[10px]" />
            </span>
          ))}
        </div>
        {remainingCount > 0 && (
          <span className="text-sm text-gray-600 truncate">{t('multiselect_overflow', { count: remainingCount })}</span>
        )}
      </>
    );
  };

  return (
    <div className={cn('relative', className)} ref={ref}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          size === 'sm' ? 'w-full px-3 h-[34px] text-left text-xs lg:h-auto lg:px-4 lg:py-3 lg:text-sm' : 'w-full px-4 py-3 text-left text-sm',
          'border border-gray-200 rounded-lg bg-white',
          'focus:outline-none focus:border-gray-400',
          !disabled && filterTriggerHover,
          'transition-all duration-200',
          'flex items-center justify-between gap-2',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {renderTriggerContent()}
        </span>
        <svg
          className={cn(
            'w-4 h-4 text-gray-400 transition-transform flex-shrink-0',
            isOpen && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-dropdown mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
          {/* Action buttons */}
          <div className="flex items-center gap-2 p-2 border-b border-gray-100">
            <button
              type="button"
              onClick={handleSelectAll}
              className="flex-1 px-3 py-1.5 text-xs font-medium text-dark-teal hover:bg-soft-mint rounded transition-colors"
            >
              {t('select_all')}
            </button>
            <button
              type="button"
              onClick={handleClearAll}
              className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded transition-colors"
            >
              {t('clear')}
            </button>
          </div>

          {/* Platform options with checkboxes */}
          {PLATFORMS.map((platform) => {
            const isSelected = value.includes(platform.value);

            return (
              <label
                key={platform.value}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm flex items-center gap-3 cursor-pointer',
                  'hover:bg-gray-50 transition-colors',
                  isSelected && 'bg-soft-mint'
                )}
              >
                {/* Checkbox */}
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleTogglePlatform(platform.value)}
                  inline
                />

                {/* Platform icon */}
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: platform.color }}
                >
                  <FontAwesomeIcon icon={platform.icon} className="text-white text-[10px]" />
                </span>

                {/* Platform name */}
                <span className={cn('flex-1', isSelected && 'text-dark-teal font-medium')}>
                  {platform.label}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};
